"use client"

import type { ChangeEvent, FunctionComponent } from "react"

import { useEffect, useState } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { Heading } from "@jobteaser/spark/components/Heading"
import { Copy } from "@jobteaser/spark/components/Icons/Copy"
import { TextInput } from "@jobteaser/spark/components/TextInput"

import { copyAsyncResponseToClipboard } from "@/modules/client/clipboard/copyAsyncResponseToClipboard"
import { createRssFeedUrl } from "@/modules/jobAds/client/api/createRssFeedUrl"
import { RssFeedModal } from "@/modules/jobAds/client/components/RssFeedModal/RssFeedModal"
import { useJobAdsSearchParams } from "@/modules/jobAds/client/hooks/useJobAdsSearchParams"
import { logger } from "@/modules/monitoring/logger"
import { useNotification } from "@/modules/notification/hooks"

import styles from "./RssFeed.module.css"

type RssFeedProps = {
  title: string
  pasteSuccessLabel: string
  inputLabel: string
  placeholder: string
  clearIconAriaLabel: string
  errorLabel: string
  submitLabel: string
  closeModalLabel: string
  titleModal: string
  detailModal: string
}

export const RssFeed: FunctionComponent<RssFeedProps> = ({
  title,
  pasteSuccessLabel,
  inputLabel,
  placeholder,
  clearIconAriaLabel,
  errorLabel,
  submitLabel,
  closeModalLabel,
  titleModal,
  detailModal,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const readOnlySearchParams = useJobAdsSearchParams()
  const searchParams = new URLSearchParams(readOnlySearchParams.toString())
  const [isValidPageSize, setValidPageSize] = useState<boolean>(false)
  const showNotification = useNotification()

  useEffect(() => {
    if (isSuccess) {
      showNotification({
        title: pasteSuccessLabel,
        variant: "success",
      })
    }
    // We remove `t` from the dependencies since it triggers an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, showNotification])

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target

    if (value && !isNaN(Number(value))) {
      setValidPageSize(true)
    } else {
      setValidPageSize(false)
    }
  }

  const handleClick = (): void => {
    setIsSuccess(false)
    setIsError(false)
    setIsLoading(true)
    const inputElement = document.getElementById("page_size")

    if (!inputElement) {
      return
    }

    // delete method functionaly working but the type not working properly : https://github.com/microsoft/TypeScript-DOM-lib-generator?tab=readme-ov-file#when-the-type-is-missing
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    searchParams.delete("locale", "none")

    const pageSize = inputElement.getAttribute("value")
    const queryParams = searchParams.toString()
    const apiUrl = `?page_size=${pageSize}${queryParams ? `&${queryParams}` : ""}`

    copyAsyncResponseToClipboard(() => createRssFeedUrl(apiUrl))
      .then(() => {
        setIsLoading(false)
        setIsSuccess(true)
      })
      .catch(error => {
        logger.error(error)
        setIsError(true)
        setIsLoading(false)
      })
  }

  return (
    <div className={styles.main}>
      <div className={styles.header}>
        <Heading as="h3" variant="title3">
          {title}
        </Heading>
        <RssFeedModal closeLabel={closeModalLabel} title={titleModal} detail={detailModal} />
      </div>

      <TextInput
        className={styles.field}
        clearIconAriaLabel={clearIconAriaLabel}
        errorMessage={isError ? errorLabel : undefined}
        hasClearIcon
        id="page_size"
        label={inputLabel}
        onChange={handleChange}
        placeholder={placeholder}
      />

      <Button
        className={styles.button}
        icon={Copy}
        onClick={handleClick}
        variant="secondary"
        disabled={!isValidPageSize || isLoading}
      >
        {submitLabel}
      </Button>
    </div>
  )
}
